import { GuestDetails } from './guestDetails';
import * as R from 'ramda';
import { IAppState } from '../core/redux/IAppState';


/**
 * Adds a new GuestDetails to the given app state and returns it while ensuring we stay under the max guestDetails count.
 * Immutable, returns a new App State object.
 */
export const addGuest = (state: IAppState, guest: GuestDetails): IAppState => ({
  ...state,
  guests:              state.guests.add(guest.masterGuestId, guest),
  knownGuestRfidCodes: R.pipe(
    value => R.concat([value], state.knownGuestRfidCodes.filter(code => !R.equals(value, code))),
  )(
    {
      firstName: guest.firstName,
      lastName:  guest.lastName,
      chipIds:   (guest.media || []).map(media => media.chipID),
    },
  ),
});


/**
 * Immutable. Takes the given app state, finds the guest by the given ID if they exist, and runs the update func on them
 * to get a new GuestDetails which replaces the old GuestDetails in a new app state -- which is returned.
 */
export const updateGuest = (
  state: IAppState,
  guestId: string,
  updateFunc: (guest: GuestDetails) => GuestDetails,
): IAppState => {
  const oldGuest = state.guests.get(guestId);

  if (!oldGuest) {
    return state;
  }

  return addGuest(state, updateFunc(oldGuest));
};


/**
 * Takes the given maybe guest and converts it into a `firstName lastName` string with no spaces on the sides. Handles
 * the case where first or last name is undefined.
 * If your maybe guest is undefined, returns empty string.
 */
export const fullName = (
  guest: { firstName: string | undefined, lastName: string | undefined } | undefined,
): string => !guest ? '' : `${guest.firstName || ''} ${guest.lastName || ''}`.trim();


/**
 * Takes the given maybe guest and converts it into a `firstName lastName` string with no spaces on the sides. Handles
 * the case where first or last name is undefined, and ensures there is no trailing comma if only last name is defined.
 * If your maybe guest is undefined, returns empty string.
 */
export const fullNameReverse = (
  guest: { firstName: string | undefined, lastName: string | undefined } | undefined,
): string => {
  const namePieces = fullName(guest).split(' ');

  if (namePieces.length === 1) {
    return namePieces[0];
  }

  return namePieces[1] + ', ' + namePieces[0];
};
